import React, { useMemo, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ChevronDown, X } from "lucide-react";

/**
 * MultiSelect - a popover-driven checkbox list with search.
 *
 * Props:
 *   options: string[] - the list of selectable values
 *   value: string[]   - currently selected values
 *   onChange: (next: string[]) => void
 *   placeholder?: string
 *   label?: string  - small label shown in the trigger when value is empty
 *   width?: string  - tailwind width class for trigger
 *   testId?: string
 */
export default function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  label,
  width = "w-[220px]",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const triggerLabel = () => {
    if (value.length === 0) return label || placeholder;
    if (value.length === 1) return value[0];
    return `${value[0]} +${value.length - 1}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex h-8 ${width} items-center justify-between gap-2 border border-neutral-300 bg-white px-2.5 text-xs text-neutral-800 hover:border-black ${
            value.length > 0 ? "border-black" : ""
          }`}
          data-testid={testId}
        >
          <span className="truncate">{triggerLabel()}</span>
          <span className="flex items-center gap-1">
            {value.length > 0 && (
              <span
                onClick={clearAll}
                role="button"
                aria-label="Clear"
                className="rounded-none p-0.5 text-neutral-500 hover:text-black"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                <X size={11} />
              </span>
            )}
            <ChevronDown size={12} className="text-neutral-500" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[280px] rounded-none border-neutral-300 p-0"
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <div className="border-b border-neutral-200 p-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="h-8 rounded-none border-neutral-300 text-xs"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {filteredOptions.length === 0 ? (
            <p className="px-3 py-2 text-[11px] text-neutral-500">No matches.</p>
          ) : (
            filteredOptions.map((opt) => {
              const checked = value.includes(opt);
              return (
                <label
                  key={opt}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors hover:bg-neutral-100 ${
                    checked ? "bg-neutral-50" : ""
                  }`}
                  data-testid={testId ? `${testId}-option-${opt}` : undefined}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(opt)}
                    className="rounded-none"
                  />
                  <span className="truncate">{opt}</span>
                </label>
              );
            })
          )}
        </div>
        {value.length > 0 && (
          <div className="flex items-center justify-between border-t border-neutral-200 px-3 py-2">
            <span className="text-[10px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              {value.length} selected
            </span>
            <button
              onClick={() => onChange([])}
              className="text-[10px] uppercase tracking-wider text-[#C50F1F] hover:underline"
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
